import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  TrendingUp, Music, User, Sparkles, ArrowRight, Play, Calendar
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrendingSection() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [trending, setTrending] = useState({ tracks: [], artists: [], events: [] });
  const [forYou, setForYou] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('trending');

  useEffect(() => {
    fetchTrending();
    if (token) {
      fetchForYou();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: mount-only fetch / stable closure
  }, [token]);

  const fetchTrending = async () => {
    try {
      const res = await axios.get(`${API}/recommendations/trending?category=all&period=week`);
      setTrending(res.data.trending || { tracks: [], artists: [], events: [] });
    } catch (err) {
      console.error('Error fetching trending:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchForYou = async () => {
    try {
      const res = await axios.get(`${API}/recommendations/for-you?limit=8`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setForYou(res.data.recommendations || []);
    } catch (err) {
      console.error('Error fetching for you:', err);
    }
  };

  if (loading) {
    return (
      <div className="py-8">
        <div className="flex justify-center">
          <div className="animate-spin w-6 h-6 border-2 border-[#00CCFF] border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="mb-12" data-testid="trending-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <TrendingUp size={20} className="text-[#00CCFF]" />
          <h2 className="text-xl font-['Chakra_Petch'] text-white uppercase">
            Discover
          </h2>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('trending')}
            className={`px-3 py-1.5 text-xs font-mono uppercase ${
              activeTab === 'trending'
                ? 'bg-[#00CCFF]/20 text-[#00CCFF] border border-[#00CCFF]'
                : 'text-[#888888] border border-[#1a1a1a] hover:text-white'
            }`}
            data-testid="trending-tab"
          >
            <TrendingUp size={12} className="inline mr-1" />
            Trending
          </button>
          {token && forYou.length > 0 && (
            <button
              onClick={() => setActiveTab('foryou')}
              className={`px-3 py-1.5 text-xs font-mono uppercase ${
                activeTab === 'foryou'
                  ? 'bg-[#9b59b6]/20 text-[#9b59b6] border border-[#9b59b6]'
                  : 'text-[#888888] border border-[#1a1a1a] hover:text-white'
              }`}
              data-testid="foryou-tab"
            >
              <Sparkles size={12} className="inline mr-1" />
              For You
            </button>
          )}
        </div>
      </div>

      {activeTab === 'trending' ? (
        <div className="grid md:grid-cols-3 gap-6">
          {/* Trending Tracks */}
          <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-4">
            <div className="flex items-center gap-2 mb-4">
              <Music size={14} className="text-[#ff6b6b]" />
              <h3 className="text-white font-mono text-sm uppercase">Hot Tracks</h3>
            </div>
            
            {trending.tracks?.length === 0 ? (
              <p className="text-[#888888] text-xs font-mono">No trending tracks yet</p>
            ) : (
              <div className="space-y-3">
                {trending.tracks?.slice(0, 5).map((track, i) => (
                  <div
                    key={track.id}
                    onClick={() => navigate(`/track/${track.slug || track.id}`)}
                    className="flex items-center gap-3 cursor-pointer group hover:bg-[#111111] p-2 -mx-2"
                  >
                    <span className="text-[#888888] font-mono text-xs w-5">{i + 1}</span>
                    <div className="w-10 h-10 bg-[#1a1a1a] flex items-center justify-center group-hover:bg-[#ff6b6b]/20">
                      <Play size={12} className="text-[#888888] group-hover:text-[#ff6b6b]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm truncate group-hover:text-[#ff6b6b]">
                        {track.title}
                      </p>
                      <p className="text-[#888888] text-xs truncate">
                        {typeof track.artist === 'object' ? track.artist?.username : track.artist}
                      </p>
                    </div>
                    <span className="text-[#888888] text-xs font-mono">{track.play_count || 0}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Trending Artists */}
          <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-4">
            <div className="flex items-center gap-2 mb-4">
              <User size={14} className="text-[#00CCFF]" />
              <h3 className="text-white font-mono text-sm uppercase">Trending Artists</h3>
            </div>
            
            {trending.artists?.length === 0 ? (
              <p className="text-[#888888] text-xs font-mono">No trending artists yet</p>
            ) : (
              <div className="space-y-3">
                {trending.artists?.slice(0, 5).map((artist, i) => (
                  <div
                    key={artist.id}
                    onClick={() => navigate(`/profile/${artist.id}`)}
                    className="flex items-center gap-3 cursor-pointer group hover:bg-[#111111] p-2 -mx-2"
                  >
                    <span className="text-[#888888] font-mono text-xs w-5">{i + 1}</span>
                    <div 
                      className="w-10 h-10 rounded-full bg-[#1a1a1a] flex items-center justify-center overflow-hidden"
                      style={{ 
                        backgroundImage: artist.avatar_url ? `url(${artist.avatar_url})` : undefined,
                        backgroundSize: 'cover'
                      }}
                    >
                      {!artist.avatar_url && (
                        <User size={14} className="text-[#888888]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm truncate group-hover:text-[#00CCFF]">
                        {artist.artist_name || artist.username}
                      </p>
                      <p className="text-[#888888] text-xs">{artist.follower_count || 0} followers</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Upcoming Events */}
          <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-4">
            <div className="flex items-center gap-2 mb-4">
              <Calendar size={14} className="text-[#9b59b6]" />
              <h3 className="text-white font-mono text-sm uppercase">Hot Events</h3>
            </div>
            
            {trending.events?.length === 0 ? (
              <p className="text-[#888888] text-xs font-mono">No upcoming events</p>
            ) : (
              <div className="space-y-3">
                {trending.events?.slice(0, 5).map((event, i) => (
                  <div
                    key={event.id}
                    onClick={() => navigate(`/event/${event.id}`)}
                    className="cursor-pointer group hover:bg-[#111111] p-2 -mx-2"
                  >
                    <p className="text-white text-sm truncate group-hover:text-[#9b59b6]">
                      {event.name || event.title || 'Untitled Event'}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-[#888888]">
                      <span>
                        {event.event_date || event.date 
                          ? new Date(event.event_date || event.date).toLocaleDateString() 
                          : 'Date TBD'}
                      </span>
                      {(event.venue || event.city) && <span>• {event.venue || event.city}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <button
              onClick={() => navigate('/events')}
              className="w-full mt-4 py-2 text-xs font-mono uppercase text-[#888888] hover:text-white border border-[#1a1a1a] hover:border-[#9b59b6]"
            >
              View All Events <ArrowRight size={12} className="inline ml-1" />
            </button>
          </div>
        </div>
      ) : (
        /* For You Recommendations */
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {forYou.map((item) => (
            <div
              key={item.id}
              onClick={() => {
                if (item.type === 'track') navigate(`/track/${item.id}`);
                else if (item.type === 'artist') navigate(`/profile/${item.id}`);
                else if (item.type === 'event') navigate(`/event/${item.id}`);
                else if (item.type === 'community') navigate(`/community/${item.id}`);
              }}
              className="border border-[#1a1a1a] bg-[#0a0a0a] p-4 cursor-pointer hover:border-[#9b59b6]/30 transition-colors group"
            >
              <div className="flex items-center gap-2 mb-2">
                {item.type === 'track' && <Music size={12} className="text-[#ff6b6b]" />}
                {item.type === 'artist' && <User size={12} className="text-[#00CCFF]" />}
                {item.type === 'event' && <Calendar size={12} className="text-[#9b59b6]" />}
                <span className="text-[#888888] text-xs font-mono uppercase">{item.type}</span>
              </div>
              
              <p className="text-white text-sm truncate group-hover:text-[#9b59b6]">
                {item.title || item.name}
              </p>
              
              {item.reason && (
                <p className="text-[#888888] text-xs font-mono mt-2 line-clamp-2">
                  <Sparkles size={10} className="inline mr-1" />
                  {item.reason}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
